import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { staffAPI } from '../../../config/api';
import APP_COLOURS from '../../../config/colors';
import StaffSalesReport from './StaffSalesReport';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function currencyFormat(num) {
  num = parseFloat(num) || 0;
  return 'R ' + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

const StaffSales = ({ staffId, staffName, staffData }) => {
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('Total sales');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    fetchSales();
  }, [staffId, startDate, endDate]);

  const fetchSales = async () => {
    if (!staffId) return;
    
    setLoading(true);
    try {
      console.log(`Fetching sales for staff ${staffId} from ${startDate} to ${endDate}`);
      const salesData = await staffAPI.getStaffSales(staffId, {
        startDate: startDate,
        endDate: endDate
      });
      
      console.log('Sales data received:', salesData);
      
      // Ensure we always set an array
      if (Array.isArray(salesData)) {
        console.log(`Setting ${salesData.length} sales records (array format)`);
        setSales(salesData);
      } else if (salesData && Array.isArray(salesData.sales)) {
        // Handle case where API returns { sales: [...] }
        console.log(`Setting ${salesData.sales.length} sales records (object.sales format)`);
        console.log('Sales summary:', {
          count: salesData.count,
          totalSales: salesData.totalSales,
          totalTransactions: salesData.totalTransactions
        });
        setSales(salesData.sales);
      } else if (salesData && typeof salesData === 'object') {
        // Handle case where API returns an object with other structure
        console.warn('Unexpected sales data structure:', salesData);
        setSales([]);
      } else {
        console.warn('No sales data received');
        setSales([]);
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingSale(null);
  };

  const handleUpdateSale = async (updateData) => {
    if (!editingSale) return;
    
    setEditLoading(true);
    try {
      // Call the API to update the sale
      await staffAPI.updateSale(staffId, editingSale.id, updateData);
      
      // Refresh sales data
      await fetchSales();
      
      // Close modal
      closeEditModal();
      
      alert('Sale updated successfully!');
    } catch (error) {
      console.error('Error updating sale:', error);
      alert('Failed to update sale: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const calculateMetrics = () => {
    const salesArray = Array.isArray(sales) ? sales : [];
    
    if (salesArray.length === 0) {
      return {
        totalSales: 0,
        averageRandSale: 0,
        itemsPerTransaction: 0,
        totalUnitsSold: 0,
      };
    }

    const totalSales = salesArray.reduce((sum, sale) => sum + (parseFloat(sale.trs) || 0), 0);
    const totalItems = salesArray.reduce((sum, sale) => {
      if (sale.items && Array.isArray(sale.items)) {
        return sum + sale.items.length;
      }
      return sum + (parseInt(sale.ipt) || 0);
    }, 0);

    return {
      totalSales,
      averageRandSale: salesArray.length > 0 ? totalSales / salesArray.length : 0,
      itemsPerTransaction: salesArray.length > 0 ? totalItems / salesArray.length : 0,
      totalUnitsSold: totalItems,
    };
  };

  const groupSalesByMonth = () => {
    const salesArray = Array.isArray(sales) ? sales : [];
    const monthlyGroups = {};

    salesArray.forEach(sale => {
      if (!sale.dateCreated) return;

      const saleDate = moment(sale.dateCreated);
      const monthKey = saleDate.format('MMM YY');

      if (!monthlyGroups[monthKey]) {
        monthlyGroups[monthKey] = {
          totalSales: 0,
          totalItems: 0,
          totalTransactions: 0,
        };
      }

      monthlyGroups[monthKey].totalSales += parseFloat(sale.trs) || 0;
      monthlyGroups[monthKey].totalTransactions += 1;
      
      if (sale.items && Array.isArray(sale.items)) {
        monthlyGroups[monthKey].totalItems += sale.items.length;
      } else {
        monthlyGroups[monthKey].totalItems += parseInt(sale.ipt) || 0;
      }
    });

    const sortedMonths = Object.keys(monthlyGroups).sort((a, b) => 
      moment(a, 'MMM YY').diff(moment(b, 'MMM YY'))
    );

    return sortedMonths.map(month => {
      const group = monthlyGroups[month];
      return {
        month,
        totalSales: group.totalSales,
        averageRandSale: group.totalTransactions > 0 ? group.totalSales / group.totalTransactions : 0,
        itemsPerTransaction: group.totalTransactions > 0 ? group.totalItems / group.totalTransactions : 0,
        totalUnitsSold: group.totalItems,
      };
    });
  };

  const groupSalesByDay = () => {
    const salesArray = Array.isArray(sales) ? sales : [];
    const dailyGroups = {};

    salesArray.forEach(sale => {
      if (!sale.dateCreated) return;

      const saleDate = moment(sale.dateCreated);
      const dayKey = saleDate.format('DD MMM');

      if (!dailyGroups[dayKey]) {
        dailyGroups[dayKey] = {
          totalSales: 0,
          totalItems: 0,
          totalTransactions: 0,
          date: saleDate,
        };
      }

      dailyGroups[dayKey].totalSales += parseFloat(sale.trs) || 0;
      dailyGroups[dayKey].totalTransactions += 1;
      
      if (sale.items && Array.isArray(sale.items)) {
        dailyGroups[dayKey].totalItems += sale.items.length;
      } else {
        dailyGroups[dayKey].totalItems += parseInt(sale.ipt) || 0;
      }
    });

    const sortedDays = Object.keys(dailyGroups).sort((a, b) => 
      dailyGroups[a].date.diff(dailyGroups[b].date)
    );

    return sortedDays.map(day => {
      const group = dailyGroups[day];
      return {
        period: day,
        totalSales: group.totalSales,
        averageRandSale: group.totalTransactions > 0 ? group.totalSales / group.totalTransactions : 0,
        itemsPerTransaction: group.totalTransactions > 0 ? group.totalItems / group.totalTransactions : 0,
        totalUnitsSold: group.totalItems,
      };
    });
  };

  const getChartData = () => {
    // Calculate the number of days in the date range
    const daysDifference = moment(endDate).diff(moment(startDate), 'days');
    const useDaily = daysDifference < 30;
    
    // Get the appropriate grouped data
    let chartData;
    if (useDaily) {
      chartData = groupSalesByDay();
    } else {
      const monthlyData = groupSalesByMonth();
      chartData = monthlyData.map(d => ({
        period: d.month,
        totalSales: d.totalSales,
        averageRandSale: d.averageRandSale,
        itemsPerTransaction: d.itemsPerTransaction,
        totalUnitsSold: d.totalUnitsSold,
      }));
    }
    
    const metricMap = {
      'Total sales': 'totalSales',
      'Average Rand Sale': 'averageRandSale',
      'Items per transaction': 'itemsPerTransaction',
      'Total units sold': 'totalUnitsSold',
    };

    const dataKey = metricMap[selectedMetric];

    return {
      labels: chartData.map(d => d.period),
      datasets: [{
        label: selectedMetric,
        data: chartData.map(d => d[dataKey]),
        borderColor: APP_COLOURS.BLUE,
        backgroundColor: `${APP_COLOURS.BLUE}20`,
        tension: 0.4,
        fill: true,
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: `${APP_COLOURS.BG5}40`,
        },
        ticks: {
          color: APP_COLOURS.TEXTCOLOR,
        },
      },
      x: {
        grid: {
          color: `${APP_COLOURS.BG5}40`,
        },
        ticks: {
          color: APP_COLOURS.TEXTCOLOR,
        },
      },
    },
  };

  // Ensure sales is always an array before filtering
  const salesArray = Array.isArray(sales) ? sales : [];
  
  const filteredSales = salesArray.filter(sale => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (sale.sale_id || '').toLowerCase().includes(query) ||
      (sale.brands || []).some(b => b.toLowerCase().includes(query)) ||
      (sale.items || []).some(item => (item.name || '').toLowerCase().includes(query))
    );
  });

  const metrics = calculateMetrics();

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
        <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading sales data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Range Filter Section */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="flex items-center gap-2 flex-wrap">
          {/* From Date */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              From
            </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 rounded text-xs focus:outline-none"
                style={{
                  backgroundColor: APP_COLOURS.BG,
                border: `1px solid ${APP_COLOURS.BG4}`,
                  color: APP_COLOURS.TEXTCOLOR,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BLUE}
                onBlur={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BG4}
              />
          </div>

          <i className="fas fa-arrow-right text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>

          {/* To Date */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              To
            </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 rounded text-xs focus:outline-none"
                style={{
                  backgroundColor: APP_COLOURS.BG,
                border: `1px solid ${APP_COLOURS.BG4}`,
                  color: APP_COLOURS.TEXTCOLOR,
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BLUE}
                onBlur={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BG4}
              />
          </div>

          {/* Action Buttons */}
            <button
              onClick={fetchSales}
            className="px-3 py-1.5 rounded text-xs font-medium"
              style={{ 
                backgroundColor: APP_COLOURS.BLUE, 
                color: '#ffffff',
              }}
          >
            <i className="fas fa-sync-alt mr-1"></i>
            Apply
            </button>
            
            <button
              onClick={() => {
                setStartDate(moment().subtract(30, 'days').format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
            className="px-3 py-1.5 rounded text-xs font-medium"
              style={{ 
                backgroundColor: APP_COLOURS.BG4,
                color: APP_COLOURS.TEXTCOLOR,
              }}
            >
            <i className="fas fa-undo mr-1"></i>
              Reset
            </button>

          <button
            onClick={() => setReportOpen(true)}
            className="px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"
            style={{
              backgroundColor: `${APP_COLOURS.BLUE}20`,
              color: APP_COLOURS.BLUE,
              border: `1px solid ${APP_COLOURS.BLUE}40`,
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}35`)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}20`)}
            title="Generate a sales report for this period"
          >
            <i className="fas fa-file-alt"></i>
            Generate Report
          </button>

          <div className="h-4 w-px" style={{ backgroundColor: APP_COLOURS.BG4 }}></div>

        {/* Quick Date Filters */}
          {[
            { label: '7d', days: 7 },
            { label: '30d', days: 30 },
            { label: '90d', days: 90 },
            { label: 'YTD', days: moment().dayOfYear() },
          ].map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setStartDate(moment().subtract(preset.days, 'days').format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
              className="px-2.5 py-1.5 rounded text-xs font-medium"
              style={{ 
                backgroundColor: APP_COLOURS.BG,
                color: APP_COLOURS.TEXTCOLOR,
                border: `1px solid ${APP_COLOURS.BG4}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                e.currentTarget.style.borderColor = APP_COLOURS.BLUE;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG;
                e.currentTarget.style.borderColor = APP_COLOURS.BG4;
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Metrics Cards */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            Sales Performance
          </h3>
          <div className="space-y-2">
            {[
              { title: 'Total sales', value: currencyFormat(metrics.totalSales) },
              { title: 'Average Rand Sale', value: currencyFormat(metrics.averageRandSale) },
              { title: 'Items per transaction', value: metrics.itemsPerTransaction.toFixed(1) },
              { title: 'Total units sold', value: metrics.totalUnitsSold },
            ].map((metric, index) => (
              <div
                key={index}
                onClick={() => setSelectedMetric(metric.title)}
                className="flex items-center justify-between p-2 rounded cursor-pointer transition-all duration-200"
                style={{
                  backgroundColor: selectedMetric === metric.title ? APP_COLOURS.BG4 : 'transparent',
                  borderLeft: selectedMetric === metric.title ? `3px solid ${APP_COLOURS.BLUE}` : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (selectedMetric !== metric.title) {
                    e.currentTarget.style.backgroundColor = APP_COLOURS.BG4 + '80';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedMetric !== metric.title) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>{metric.title}</span>
                <span className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>{metric.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Performance History
            </h3>
            <span className="text-xs px-2 py-0.5 rounded" style={{ 
              backgroundColor: APP_COLOURS.BG4, 
              color: APP_COLOURS.PLACEHOLDER 
            }}>
              {moment(endDate).diff(moment(startDate), 'days') < 30 ? 'Daily' : 'Monthly'}
            </span>
          </div>
          <div style={{ height: '200px' }}>
            <Line data={getChartData()} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Sales List */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            Recent Sales ({filteredSales.length})
          </h3>
            <div className="relative">
            <i className="fas fa-search absolute left-2.5 top-1/2 transform -translate-y-1/2 text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
              <input
                type="text"
                placeholder="Search sales..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 rounded text-xs focus:outline-none"
                style={{
                  backgroundColor: APP_COLOURS.BG,
                border: `1px solid ${APP_COLOURS.BG4}`,
                  color: APP_COLOURS.TEXTCOLOR,
                minWidth: '200px',
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BLUE}
                onBlur={(e) => e.currentTarget.style.borderColor = APP_COLOURS.BG4}
              />
          </div>
        </div>

        {/* Sales Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: APP_COLOURS.BG4 }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Items</th>
                <th className="px-3 py-2 text-center text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Qty</th>
                <th className="px-3 py-2 text-right text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Total</th>
                <th className="px-3 py-2 text-center text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.length > 0 ? (
                filteredSales
                  .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
                  .map((sale, index) => (
                      <tr
                      key={index}
                      onClick={() => openEditModal(sale)}
                      className="cursor-pointer transition-all duration-200"
                        style={{
                          backgroundColor: index % 2 === 0 ? 'transparent' : `${APP_COLOURS.BG4}40`,
                          borderBottom: `1px solid ${APP_COLOURS.BG4}`,
                        }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${APP_COLOURS.BG4}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : `${APP_COLOURS.BG4}40`;
                      }}
                    >
                      <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: APP_COLOURS.BLUE }}>
                        {moment(sale.dateCreated).format('DD MMM')}
                        </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          {(sale.items || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <span className="text-xs truncate max-w-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                {item.name || 'Unknown item'}
                              </span>
                              <span className="text-xs whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                                ×{item.quantity || 1}
                              </span>
                              {item.returned && (
                                <span className="text-xs px-1 py-0.5 rounded whitespace-nowrap" style={{ 
                                  backgroundColor: APP_COLOURS.ORANGE + '20',
                                  color: APP_COLOURS.ORANGE 
                                }}>
                                  Returned
                              </span>
                            )}
                            </div>
                          ))}
                          </div>
                        </td>
                      <td className="px-3 py-2 text-xs text-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                          {sale.ipt || (sale.items && sale.items.length) || 0}
                        </td>
                      <td className="px-3 py-2 text-xs text-right font-semibold whitespace-nowrap" style={{ color: APP_COLOURS.GREEN }}>
                          {currencyFormat(sale.trs || 0)}
                        </td>
                      <td className="px-3 py-2 text-center">
                            <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(sale);
                          }}
                          className="px-2 py-1 rounded text-xs"
                              style={{ 
                                backgroundColor: APP_COLOURS.BLUE,
                                color: '#ffffff',
                              }}
                          title="View Sale"
                        >
                          <i className="fas fa-eye"></i>
                            </button>
                        </td>
                      </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-8 text-center">
                    <i className="fas fa-shopping-bag text-3xl mb-2" style={{ color: APP_COLOURS.BG5 }}></i>
                    <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      No sales data found
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Sale Modal */}
      {editModalOpen && editingSale && (
        <EditSaleModal
          sale={editingSale}
          onClose={closeEditModal}
          onUpdate={handleUpdateSale}
          loading={editLoading}
        />
      )}

      {/* Sales Report Modal */}
      {reportOpen && (
        <StaffSalesReport
          staffData={staffData || { id: staffId, first_name: staffName }}
          initialStartDate={startDate}
          initialEndDate={endDate}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
};

// Edit Sale Modal Component
const EditSaleModal = ({ sale, onClose, onUpdate, loading }) => {
  const [activeTab, setActiveTab] = useState('receipt');
  const [newDate, setNewDate] = useState(moment(sale.dateCreated).format('YYYY-MM-DD'));
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [newPrice, setNewPrice] = useState('');

  const handleUpdateDate = () => {
    if (!newDate) {
      alert('Please select a valid date');
      return;
    }
    onUpdate({
      action: 'UPDATE_DATE',
      data: { newDate }
    });
  };

  const handleRemoveItem = (itemIndex) => {
    if (window.confirm('Are you sure you want to remove this item from the sale?')) {
      onUpdate({
        action: 'REMOVE_ITEM',
        data: { itemIndex }
      });
    }
  };

  const handleUpdatePrice = () => {
    if (selectedItemIndex === null) {
      alert('Please select an item');
      return;
    }
    const priceValue = parseFloat(newPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      alert('Please enter a valid price');
      return;
    }
    onUpdate({
      action: 'UPDATE_ITEM_PRICE',
      data: { itemIndex: selectedItemIndex, newPrice: priceValue }
    });
  };

  const handleCreditItem = (itemIndex) => {
    if (window.confirm('Are you sure you want to credit this item as a return?')) {
      onUpdate({
        action: 'CREDIT_ITEM',
        data: { itemIndex }
      });
    }
  };

  const calculateTotal = () => {
    if (!sale.items || !Array.isArray(sale.items)) return 0;
    return sale.items.reduce((sum, item) => {
      if (item.returned) return sum;
      return sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1);
    }, 0);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: APP_COLOURS.BG3 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: APP_COLOURS.BG4 }}>
            <div>
            <h2 className="text-lg font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              Sale Details
              </h2>
            <p className="text-xs mt-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              {moment(sale.dateCreated).format('DD MMM YYYY')} • {currencyFormat(sale.trs || 0)}
              </p>
            </div>
            <button
              onClick={onClose}
            className="p-1.5 rounded transition-all duration-200"
              style={{ 
                backgroundColor: APP_COLOURS.BG4,
                color: APP_COLOURS.TEXTCOLOR,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = APP_COLOURS.BG5}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = APP_COLOURS.BG4}
            >
            <i className="fas fa-times text-sm"></i>
            </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b overflow-x-auto" style={{ borderColor: APP_COLOURS.BG4, backgroundColor: APP_COLOURS.BG }}>
          {[
            { id: 'receipt', label: 'Receipt', icon: 'fa-receipt' },
            { id: 'date', label: 'Date', icon: 'fa-calendar' },
            { id: 'remove', label: 'Remove', icon: 'fa-trash' },
            { id: 'price', label: 'Price', icon: 'fa-dollar-sign' },
            { id: 'credit', label: 'Credit', icon: 'fa-undo' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 px-3 py-2.5 text-xs font-medium transition-all duration-200 whitespace-nowrap"
              style={{
                color: activeTab === tab.id ? APP_COLOURS.BLUE : APP_COLOURS.PLACEHOLDER,
                borderBottom: activeTab === tab.id ? `2px solid ${APP_COLOURS.BLUE}` : '2px solid transparent',
                backgroundColor: activeTab === tab.id ? APP_COLOURS.BG3 : 'transparent',
              }}
            >
              <i className={`fas ${tab.icon} mr-1.5`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 150px)' }}>
          {/* Receipt Tab */}
          {activeTab === 'receipt' && (
            <div className="max-w-2xl mx-auto">
              {/* Receipt Header */}
              <div className="text-center mb-4 pb-3" style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}` }}>
                <h3 className="text-base font-bold mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Sale Receipt
                </h3>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  {moment(sale.dateCreated).format('DD MMMM YYYY • HH:mm')}
                </p>
                {sale.sale_id && (
                  <p className="text-xs mt-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                    ID: {sale.sale_id}
                  </p>
                )}
              </div>

              {/* Customer & Location Info - Combined */}
              {((sale.customer_gender || sale.customer_age_range) || sale.mall) && (
                <div className="mb-3 p-3 rounded-lg grid grid-cols-2 gap-3" style={{ backgroundColor: APP_COLOURS.BG }}>
                  {(sale.customer_gender || sale.customer_age_range) && (
                    <div>
                      <h4 className="text-xs font-semibold mb-1.5 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        <i className="fas fa-user mr-1.5 text-xs" style={{ color: APP_COLOURS.BLUE }}></i>
                        Customer
                      </h4>
                      <div className="text-xs space-y-0.5">
                        {sale.customer_gender && (
                          <div>
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Gender: </span>
                            <span style={{ color: APP_COLOURS.TEXTCOLOR }}>{sale.customer_gender}</span>
                          </div>
                        )}
                        {sale.customer_age_range && (
                          <div>
                            <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Age: </span>
                            <span style={{ color: APP_COLOURS.TEXTCOLOR }}>{sale.customer_age_range}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {sale.mall && (
                    <div>
                      <h4 className="text-xs font-semibold mb-1.5 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        <i className="fas fa-map-marker-alt mr-1.5 text-xs" style={{ color: APP_COLOURS.BLUE }}></i>
                        Location
                      </h4>
                      <p className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>{sale.mall}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Items List */}
              <div className="mb-3">
                <h4 className="text-xs font-semibold mb-2 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  <i className="fas fa-shopping-bag mr-1.5 text-xs" style={{ color: APP_COLOURS.BLUE }}></i>
                  Items ({(sale.items || []).length})
                </h4>
                <div className="space-y-1.5">
                  {(sale.items || []).map((item, index) => (
                    <div
                      key={index}
                      className="p-2.5 rounded-lg"
                      style={{ 
                        backgroundColor: APP_COLOURS.BG,
                        opacity: item.returned ? 0.6 : 1,
                      }}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex-1">
                          <p className="text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {item.name || 'Unknown item'}
                          </p>
                          {item.brand && (
                            <p className="text-xs mt-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                              {item.brand}
                            </p>
                          )}
                        </div>
                        {item.returned && (
                          <span className="text-xs px-1.5 py-0.5 rounded ml-2 whitespace-nowrap" style={{ 
                            backgroundColor: APP_COLOURS.ORANGE + '20',
                            color: APP_COLOURS.ORANGE 
                          }}>
                            <i className="fas fa-undo mr-1"></i>
                            Returned
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <div style={{ color: APP_COLOURS.PLACEHOLDER }}>
                          <span>Qty: {item.quantity || 1}</span>
                          <span className="mx-1.5">×</span>
                          <span>{currencyFormat(item.price || 0)}</span>
                        </div>
                        <div className="font-semibold" style={{ 
                          color: item.returned ? APP_COLOURS.PLACEHOLDER : APP_COLOURS.TEXTCOLOR,
                          textDecoration: item.returned ? 'line-through' : 'none'
                        }}>
                          {currencyFormat((item.price || 0) * (item.quantity || 1))}
                        </div>
                      </div>
                      {item.returned && item.returnedDate && (
                        <p className="text-xs mt-1.5 pt-1.5" style={{ 
                          color: APP_COLOURS.PLACEHOLDER,
                          borderTop: `1px solid ${APP_COLOURS.BG4}`
                        }}>
                          <i className="fas fa-calendar mr-1"></i>
                          {moment(item.returnedDate).format('DD MMM YYYY')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Brands */}
              {sale.brands && sale.brands.length > 0 && (
                <div className="mb-3 p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG }}>
                  <h4 className="text-xs font-semibold mb-2 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    <i className="fas fa-tag mr-1.5 text-xs" style={{ color: APP_COLOURS.BLUE }}></i>
                    Brands
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {sale.brands.map((brand, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: APP_COLOURS.BLUE + '20',
                          color: APP_COLOURS.BLUE
                        }}
                      >
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sale Summary */}
              <div className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG4 }}>
                <h4 className="text-xs font-semibold mb-2 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  <i className="fas fa-calculator mr-1.5 text-xs" style={{ color: APP_COLOURS.BLUE }}></i>
                  Summary
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Average Rand Sale</span>
                    <span className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {currencyFormat(sale.ars || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: APP_COLOURS.PLACEHOLDER }}>Items Count</span>
                    <span className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {sale.ipt || (sale.items && sale.items.length) || 0}
                    </span>
                  </div>
                  <div className="pt-2 mt-2" style={{ borderTop: `1px solid ${APP_COLOURS.BG5}` }}>
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        Total Rand Sale
                      </span>
                      <span className="text-base font-bold" style={{ color: APP_COLOURS.GREEN }}>
                        {currencyFormat(sale.trs || 0)}
                      </span>
                    </div>
                  </div>
                  {sale.hasReturns && (
                    <div className="pt-2 flex justify-between text-xs">
                      <span style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        Active Total (excl. returns)
                      </span>
                      <span className="font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {currencyFormat(calculateTotal())}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Change Date Tab */}
          {activeTab === 'date' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Select New Date
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full px-3 py-2 rounded text-xs focus:outline-none"
                  style={{
                    backgroundColor: APP_COLOURS.BG,
                    border: `1px solid ${APP_COLOURS.BG4}`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                />
              </div>
              <div className="p-2.5 rounded" style={{ backgroundColor: APP_COLOURS.BG }}>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  <i className="fas fa-info-circle mr-1.5"></i>
                  Current: <span style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    {moment(sale.dateCreated).format('DD MMMM YYYY')}
                  </span>
                </p>
              </div>
              <button
                onClick={handleUpdateDate}
                disabled={loading}
                className="w-full px-4 py-2 rounded text-xs font-medium"
                style={{ 
                  backgroundColor: APP_COLOURS.BLUE,
                  color: '#ffffff',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? 'Updating...' : 'Update Date'}
              </button>
            </div>
          )}

          {/* Remove Item Tab */}
          {activeTab === 'remove' && (
            <div className="space-y-3">
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                Select an item to remove. Total will be recalculated automatically.
              </p>
              <div className="space-y-1.5">
                {(sale.items || []).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 rounded-lg"
                    style={{ 
                      backgroundColor: APP_COLOURS.BG,
                      opacity: item.returned ? 0.5 : 1,
                    }}
                  >
                    <div className="flex-1">
                      <p className="text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {item.name || 'Unknown item'}
                        {item.returned && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ 
                            backgroundColor: APP_COLOURS.ORANGE + '20',
                            color: APP_COLOURS.ORANGE 
                          }}>
                            Returned
                          </span>
                        )}
                      </p>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        Qty: {item.quantity || 1} × {currencyFormat(item.price || 0)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(index)}
                      disabled={loading || item.returned}
                      className="px-3 py-1.5 rounded text-xs font-medium"
                      style={{ 
                        backgroundColor: APP_COLOURS.RED,
                        color: '#ffffff',
                        opacity: (loading || item.returned) ? 0.4 : 1,
                      }}
                    >
                      <i className="fas fa-trash mr-1"></i>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Update Price Tab */}
          {activeTab === 'price' && (
            <div className="space-y-3">
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                Select an item and enter new price. Total will be recalculated automatically.
              </p>
              <div className="space-y-1.5">
                {(sale.items || []).map((item, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      if (!item.returned) {
                        setSelectedItemIndex(index);
                        setNewPrice(item.price || '');
                      }
                    }}
                    className="flex items-center justify-between p-2.5 rounded-lg cursor-pointer"
                    style={{ 
                      backgroundColor: selectedItemIndex === index ? APP_COLOURS.BLUE + '20' : APP_COLOURS.BG,
                      border: `1px solid ${selectedItemIndex === index ? APP_COLOURS.BLUE : 'transparent'}`,
                      opacity: item.returned ? 0.5 : 1,
                      cursor: item.returned ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <div className="flex-1">
                      <p className="text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {item.name || 'Unknown item'}
                        {item.returned && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ 
                            backgroundColor: APP_COLOURS.ORANGE + '20',
                            color: APP_COLOURS.ORANGE 
                          }}>
                            Returned
                          </span>
                        )}
                      </p>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        Current: {currencyFormat(item.price || 0)}
                      </p>
                    </div>
                    {selectedItemIndex === index && (
                      <i className="fas fa-check-circle text-sm" style={{ color: APP_COLOURS.BLUE }}></i>
                    )}
                  </div>
                ))}
              </div>
              {selectedItemIndex !== null && (
                <div className="space-y-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG }}>
                  <label className="block text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    New Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="Enter new price"
                    className="w-full px-3 py-2 rounded text-xs focus:outline-none"
                    style={{
                      backgroundColor: APP_COLOURS.BG3,
                      border: `1px solid ${APP_COLOURS.BG4}`,
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  />
                  <button
                    onClick={handleUpdatePrice}
                    disabled={loading}
                    className="w-full px-4 py-2 rounded text-xs font-medium"
                    style={{ 
                      backgroundColor: APP_COLOURS.BLUE,
                      color: '#ffffff',
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Price'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Credit Return Tab */}
          {activeTab === 'credit' && (
            <div className="space-y-3">
              <div className="p-2.5 rounded" style={{ backgroundColor: APP_COLOURS.ORANGE + '20' }}>
                <p className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  <i className="fas fa-info-circle mr-1.5" style={{ color: APP_COLOURS.ORANGE }}></i>
                  Marks item as returned. Won't count towards total.
                </p>
              </div>
              <div className="space-y-1.5">
                {(sale.items || []).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 rounded-lg"
                    style={{ 
                      backgroundColor: APP_COLOURS.BG,
                      opacity: item.returned ? 0.5 : 1,
                    }}
                  >
                    <div className="flex-1">
                      <p className="text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {item.name || 'Unknown item'}
                        {item.returned && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ 
                            backgroundColor: APP_COLOURS.ORANGE + '20',
                            color: APP_COLOURS.ORANGE 
                          }}>
                            Already Returned
                          </span>
                        )}
                      </p>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        Qty: {item.quantity || 1} × {currencyFormat(item.price || 0)} = {currencyFormat((item.price || 0) * (item.quantity || 1))}
                      </p>
                      {item.returned && item.returnedDate && (
                        <p className="text-xs mt-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                          {moment(item.returnedDate).format('DD MMM YYYY')}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleCreditItem(index)}
                      disabled={loading || item.returned}
                      className="px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap"
                      style={{ 
                        backgroundColor: APP_COLOURS.ORANGE,
                        color: '#ffffff',
                        opacity: (loading || item.returned) ? 0.4 : 1,
                      }}
                    >
                      <i className="fas fa-undo mr-1"></i>
                      Credit
                    </button>
                  </div>
                ))}
              </div>
              {sale.hasReturns && (
                <div className="p-2.5 rounded" style={{ backgroundColor: APP_COLOURS.BG }}>
                  <p className="text-xs font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    <i className="fas fa-calculator mr-1.5"></i>
                    Active Total: {currencyFormat(calculateTotal())}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t flex justify-end" style={{ borderColor: APP_COLOURS.BG4 }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded text-xs font-medium"
            style={{ 
              backgroundColor: APP_COLOURS.BG4,
              color: APP_COLOURS.TEXTCOLOR,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffSales;

